import { UtilData } from '@/misc/UtilData';
import { action, computed, extendObservable, observable, makeObservable } from 'mobx';
import { UsuarioStore } from '../store';
import LayoutStore from '../store/LayoutStore';
import MensagemStore from '../store/MensagemStore';
import RouterStore from '../store/RouterStore';
import NumeroProcesso from '../utils/NumeroProcesso';
import { IAssunto } from './Assunto';
import { ControleGabinete } from './ControleGabinete';
import { Peca } from './dominio/Peca';
import { Relator } from './dominio/Relator';
import { AlertaDto } from './dto/AlertaDto';
import { ClasseJudicialDto } from './dto/ClasseJudicialDto';
import { OrgaoJulgadorDto } from './dto/OrgaoJulgadorDto';
import { PecaDto } from './dto/PecaDto';
import { EtiquetaOrigem, IEtiqueta } from './Etiquetas';
import { IIndicadores } from './Indicadores';
import { Minuta } from './Minuta';
import { IMovimento } from './Movimento';
import { IOrigemProcesso } from './OrigemProcesso';
import { IPartes } from './Partes';
import { Pauta } from './Pauta';
import { PeticaoPendente } from './PeticaoApreciada';
import { ProcessoAssociado, ProcessoComCorreJunto } from './ProcessoRelacionamentos';
import { IResultadoJulgamento } from './ResultadoJulgamento';
import { ITarefaFluxoPje } from './TarefaFluxoPje';
import { Tema } from './Tema';
import { TipoMinuta } from './TipoMinuta';
import ProcessoTipoJulgamento, { TipoJulgamentoEnum } from './TipoJulgamento';
import { Triagem } from './Triagem';

export class Processo {
    @observable accessor minutas: Minuta[] = [];

    constructor() {
        this.correJuntos = [];
        this.processosAssociados = [];

        makeObservable(this, {
            relator: observable,
            relatorOriginal: observable,
            correJuntos: observable,
            processosAssociados: observable,
            adicionarMinuta: action,
            removeMinuta: action,
            selecionar: action,
            resetMostrar: action,
            selecionarMinuta: action,
            deselecionarMinutas: action,
            setMostrar: action,
            setRelator: action,
            setBackground: action,
            atualizarBackground: action,
            selecionado: computed,
            mostrar: computed,
            minutasVisiveis: computed,
            peticoesPendentesVisiveis: computed,
            quantidadePeticoesPendentes: computed,
            ultimoMovimentoCNJ: computed
        });
    }

    static mobxInicial = () => ({
        partes: [],
        idsMinutasComentadas: [],
        assuntosProcesso: [],
        indicadores: [],
        etiquetas: [],
        pecas: [],
        triagemLivre: '',
        triagemTemas: undefined,
        triagem: undefined,
        temas: [],
        _mostrar: true,
        _selecionado: false,
        _pendencia: '',
        peticoesPendentes: [],
        resultadoJulgamentoProcesso: undefined,
        situacaoDeInclusaoEmPauta: '',
        pauta: undefined,
        pecasPeticoesPendentes: [],
        tipoJulgamento: undefined,
        background: LayoutStore.backgroundProcessoPadrao,
        impedimentos: [],
        movimentos: [],
        ultimoMovimentoProcessual: undefined,
        agravoProvido: false,
        favorito: false,
        retorno: false,
        controleGabinete: undefined,
        numeroCpfResponsavel: '',
        get pendencia() {
            return this._pendencia.trim();
        },
        set pendencia(pendencia: string) {
            this._pendencia = pendencia
        },
        // correJuntos: [] as ProcessoComCorreJunto[],
        // processosAssociados: [] as ProcessoAssociado[]
    })

    static novo(processoDto: Partial<Processo>) {

        if (!processoDto) {
            processoDto = {};
        }
        // Inicialização defensiva de arrays
        if (!Array.isArray(processoDto.impedimentos)) processoDto.impedimentos = [];
        if (!Array.isArray(processoDto.minutas)) processoDto.minutas = [];
        if (!Array.isArray(processoDto.etiquetas)) processoDto.etiquetas = [];
        if (!Array.isArray(processoDto.correJuntos)) processoDto.correJuntos = [];
        if (!Array.isArray(processoDto.processosAssociados)) processoDto.processosAssociados = [];
        const procGab = new Processo();
        const mobxInicial = Processo.mobxInicial();
        extendObservable(procGab, mobxInicial);
        processoDto.minutas = processoDto.minutas?.map(minuta => {
            return Minuta.criar(minuta, processoDto as Processo);
        }) || [];

        if (processoDto.numeroProcesso == undefined && processoDto.numero) {
            processoDto.numeroProcesso = processoDto.numero;
        }

        if (!processoDto.classe) {
            if ((processoDto as any).classesJudicial?.length > 0) {
                const classes = (processoDto as any).classesJudicial;
                processoDto.classe = classes.map(c => c.sigla).join('-');
            } else {
                processoDto.classe = processoDto.classes || processoDto.classeJudicial?.sigla || processoDto.numeroProcesso && NumeroProcesso.valueOf(processoDto.numeroProcesso).getFase() || '';
            }
        }

        processoDto.classe = processoDto.classe.replace(/ - /g, '-');
        procGab.minutas = (procGab.minutas || []).filter(minuta => minuta && minuta.numero);
        processoDto.numeroProcesso = processoDto.classe + ' - ' + (processoDto.numeroProcesso && NumeroProcesso.valueOf(processoDto.numeroProcesso).semFase().numeroProcessoFormatado())

        // Filtrar pelo mesmo OJ
        if (!Array.isArray(processoDto.impedimentos)) {
            processoDto.impedimentos = [];
        }
        if (processoDto.impedimentos.length > 0) {
            const siglaOJ = UsuarioStore.gabinete;
            processoDto.impedimentos = processoDto.impedimentos.filter(imp => {
                return imp && imp.includes && imp.includes(siglaOJ);
            });
        }
        if (processoDto.quantidadePeticoesPendentes != undefined) {
            // Não usar `delete`: quando processoDto já é uma instância de Processo (ex.: reprocessada
            // via envelope), essa propriedade é um computed do MobX, não configurável, e o delete lança
            // TypeError. Reconstruir o objeto sem o campo evita a mutação e funciona nos dois casos.
            const { quantidadePeticoesPendentes, ...resto } = processoDto as any;
            processoDto = resto;
        }

        const procFinal = Object.assign(procGab, processoDto);
        if (!procFinal.tipoJulgamento) {
            procFinal.tipoJulgamento = new ProcessoTipoJulgamento();
            procFinal.tipoJulgamento.tipoJulgamento = TipoJulgamentoEnum.PRESENCIAL;
        }
        procFinal.etiquetas = procFinal.etiquetas || [];

        if (procFinal.dataAutuacaoTst && typeof (procFinal.dataAutuacaoTst) != 'string') {
            const ano = (procFinal.dataAutuacaoTst as any)?.date.year;
            let mes = (procFinal.dataAutuacaoTst as any)?.date.month;
            mes = mes.toString().length < 2 ? '0' + mes.toString() : mes.toString();
            let dia = (procFinal.dataAutuacaoTst as any)?.date.day;
            dia = dia.toString().length < 2 ? '0' + dia.toString() : dia.toString();
            let hora = (procFinal.dataAutuacaoTst as any)?.time.hour;
            hora = hora.toString().length < 2 ? '0' + hora.toString() : hora.toString();
            let minuto = (procFinal.dataAutuacaoTst as any)?.time.minute;
            minuto = minuto.toString().length < 2 ? '0' + minuto.toString() : minuto.toString();
            let seg = (procFinal.dataAutuacaoTst as any)?.time.second;
            seg = seg.toString().length < 2 ? '0' + seg.toString() : seg.toString();
            (procFinal.dataAutuacaoTst as any) = ano + '-' + mes + '-' + dia + 'T' + hora + ':' + minuto + ':' + seg;
        }

        if (!procFinal.autuadoEm) {
            procFinal.autuadoEm = procFinal.dataAutuacaoTst;
        }
        procFinal.pauta && (procFinal.pauta.dataSessao = UtilData.formataData(procFinal.pauta.dataSessao));

        return procFinal;
    }

    situacaoSessao: string;
    // minutas: Minuta[];
    retorno: boolean;
    triagem: Triagem;
    triagemLivre: string;
    temas: Tema[];
    idsMinutasComentadas: number[];
    indicadores: IIndicadores[];
    classeJudicial: ClasseJudicialDto;
    partes: IPartes;
    pecas: Peca[];
    situacaoDeInclusaoEmPauta: string;
    alertas: AlertaDto[];
    tarefaFluxoPje: ITarefaFluxoPje;
    relator: Relator;
    relatorOriginal: Relator;
    orgaoJulgador: OrgaoJulgadorDto;
    sigUnAdm: string;
    movimentos: IMovimento[];
    ultimoMovimentoProcessual?: IMovimento;
    autuadoEm: string;
    distribuidoEm: string;
    valorDaCausa: number;
    assuntosProcesso: IAssunto[];
    // minutaHtml: boolean;
    private _selecionado: boolean;
    peticoesPendentes: PeticaoPendente[];
    pecasPeticoesPendentes: PecaDto[];
    resultadoJulgamentoProcesso: IResultadoJulgamento;
    etiquetas: IEtiqueta[];
    etiquetasOrigem: EtiquetaOrigem[];
    segredoDeJustica: boolean;
    favorito: boolean;
    agravoProvido: boolean;
    tipoJulgamento: ProcessoTipoJulgamento;
    pauta?: Pauta;
    impedimentos: string[];
    correJuntos: ProcessoComCorreJunto[];
    processosAssociados: ProcessoAssociado[];

    //  Inicio BotaoMovimentacaoInterna TODO remover daqui
    pendencia: string;
    _mostrar: boolean;

    //Atributos da tela de assinatura TODO: Separar com conceito de Bounded Context
    alterado: boolean;
    vistoLiberado: boolean;
    background: string;
    backgroundPadrao: string;
    classes?: string;

    identificadorProcesso: number;
    numeroProcesso: string;
    numero: string;
    numeroCpfResponsavel: string;
    orgaoJulgadorColegiado: OrgaoJulgadorDto;
    // minutaAssociada: Minuta;
    origemProcesso: IOrigemProcesso; //TODO arrumar o DTO
    dataAutuacaoTst: string;
    controleGabinete: ControleGabinete;
    dataUltimaModificacao: Date;
    processoPautaFutura?: boolean;

    classe?: string; //TODO remover atributo mutavel
    // indicador?: string; //TODO remover atributo mutavel
    // itemColor?: string; //TODO remover atributo mutavel

    adicionarMinuta(minuta: Minuta) {
        this.minutas.push(minuta);
        this.minutas = this.minutas.slice().sort((m1, m2) => m1.tipo?.codigo - m2.tipo?.codigo)
    }

    adicionarMinutaNoAction(minuta: Minuta) {
        this.minutas.push(minuta);
        this.minutas = this.minutas.slice().sort((m1, m2) => m1.tipo?.codigo - m2.tipo?.codigo)
    }

    removeMinuta(minuta: Minuta) {
        const minutaEncontrada = this.minutas.findIndex((iMinuta: Minuta) => iMinuta.tipo.codigo === minuta.tipo.codigo);
        if (minutaEncontrada > -1) {
            this.minutas.splice(minutaEncontrada, 1);
        }
    }

    /** Minutas não ocultadas pelo filtro atual (respeita `minuta.mostrar`). */
    get minutasVisiveis(): Minuta[] {
        return this.minutas.filter(minuta => minuta && minuta.mostrar !== false);
    }

    selecionar(valor: boolean) {
        if (this.minutas.length === 0) {
            if (this._selecionado !== valor) {
                this._selecionado = valor;
            }
        } else {
            // Ao marcar, seleciona apenas as minutas visíveis (respeita o filtro);
            // ao desmarcar, desmarca todas.
            const alvo = valor ? this.minutasVisiveis : this.minutas;
            alvo.forEach(minuta => {
                if (!minuta) return;
                if (minuta.selecionada !== valor) {
                    minuta.selecionar(valor);
                }
            });
        }
    }

    resetMostrar() {
        if (this._mostrar !== true) {
            this._mostrar = true;
        }
        this.minutas.forEach(min => {
            if (!min) return;
            if (min.mostrar !== true) {
                min.mostrar = true;
            }
        });
    }


    get selecionado(): boolean {
        return this.minutas.length === 0 ? this._selecionado : this.minutas.some(minuta => minuta?.selecionada);
    }

    get mostrar(): boolean {
        if (this.minutas.length === 0) {
            return this._mostrar;
        } else {
            return this.minutas.some(minuta => minuta?.mostrar);
        }
    }

    get peticoesPendentesVisiveis(): PeticaoPendente[] {
        const peticoes = this.peticoesPendentes ?? [];

        if (!this.ehLegado) {
            return peticoes;
        }

        const ehGabinete = !UsuarioStore.gabinete?.startsWith('SE');

        return peticoes.filter(peticao => {
            const situacao = peticao?.situacaoPeticao;
            if (Processo.situacaoPeticaoNaoPendente(situacao)) {
                return false;
            }
            if (ehGabinete && Processo.situacaoPeticaoExclusivaSecretaria(situacao)) {
                return false;
            }
            return true;
        });
    }

    private static normalizarSituacaoPeticao(texto?: string): string {
        return (texto ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
    }

    static situacaoPeticaoNaoPendente(situacao?: string): boolean {
        const situacoesNaoPendentes = [
            'aguardando cadastramento',
            'devolvida/arquivada',
            'para devolver',
            'peticao apreciada',
            'apreciada'
        ];
        return situacoesNaoPendentes.includes(Processo.normalizarSituacaoPeticao(situacao));
    }

    static situacaoPeticaoExclusivaSecretaria(situacao?: string): boolean {
        const situacoesExclusivasSecretaria = [
            'aguardando despacho da segjud',
            'aguardando informacao',
            'despacho em fase de cumprimento'
        ];
        return situacoesExclusivasSecretaria.includes(Processo.normalizarSituacaoPeticao(situacao));
    }

    get quantidadePeticoesPendentes() {
        return this.peticoesPendentesVisiveis?.length > 0 ? this.peticoesPendentesVisiveis?.length : 0;
    }

    selecionarMinuta(numero?: number): void {
        this.deselecionarMinutas();
        const minuta = this.minutas.find(minuta => minuta.numero === numero);
        if (minuta && typeof minuta?.selecionar === 'function') {
            minuta.selecionar(true);
        }
    }

    deselecionarMinutas() {
        this.minutas.forEach(minuta => {
            if (typeof minuta?.selecionar === 'function') {
                minuta.selecionar(false);
            }
        });
    }

    setMostrar(valor: boolean, _setMinutas: boolean = false) {
        if (this._mostrar !== valor) {
            this._mostrar = valor;
        }
        this.minutas.forEach(minuta => {
            if (typeof minuta?.setMostrar === 'function') {
                const proximoValor = minuta.mostrar && valor;
                if (minuta.mostrar !== proximoValor) {
                    minuta.setMostrar(proximoValor);
                }
            } else {
                console.warn('Minuta inválida:', minuta);
            }
        });
    }

    setRelator(relator: Relator) {
        this.relator = relator;
    }

    setBackground = () => {
        this.background = LayoutStore.backgroundProcesso;
        setTimeout(this.atualizarBackground, 100);
    }

    atualizarBackground = () => {
        if (!this.background) {
            this.background = "#a0a1ef"
        }
        const numero = (Number('0x' + this.background.substring(1))).valueOf();
        if (LayoutStore.isTemaDark ? numero > 0x2E2E2E : numero < 0xFEFEFE) {
            const somaTransicao = LayoutStore.isTemaDark ? -0x02 : 0x02;
            let r = ((Number('0x' + this.background.substring(1, 3))) + somaTransicao).toString(16);
            r = parseInt(r) < 0 ? '0' : r;
            let g = ((Number('0x' + this.background.substring(3, 5))) + somaTransicao).toString(16);
            g = parseInt(g) < 0 ? '0' : g;
            const b = LayoutStore.isTemaDark ? ((Number('0x' + this.background.substring(5))) + somaTransicao).toString(16) : 'ff';
            this.background = '#' + r + g + b;
            setTimeout(this.atualizarBackground, 30);
        } else {
            this.background = this.backgroundPadrao?.length > 0 ? this.backgroundPadrao : LayoutStore.backgroundProcessoPadrao;
        }
    }

    etiquetaJaExiste = (textoNovaEtiqueta: string) => {
        const etiqueta = this.etiquetas.find(tag => tag.texto.toUpperCase().indexOf(textoNovaEtiqueta.toUpperCase()) > -1);
        if (etiqueta) {
            MensagemStore.apresentarErro(`A etiqueta informada já está associada ao processo.`);
        }
        return etiqueta;
    }

    get siglaRelator() {
        return this.relatorOriginal?.sigla ? this.relatorOriginal.sigla : (typeof (this.relatorOriginal) === 'string' ? this.relator : '')
    }

    // TODO metodos abaixo foram criados para manter compatibilidade com o código do BotaoMovimentacaoInterna
    get minutaSelected(): Minuta {
        return this.minutas.find(minuta => minuta?.selecionada);
    }

    get minutaSelecionada(): number {
        return this.minutaSelected && this.minutaSelected.numero;
    }

    get tipoMinutaSelecionada(): number {
        return (this.minutaSelected && this.minutaSelected.tipo.codigo) || -1;
    }

    set minutaSelecionada(numero: number) {
        this.selecionarMinuta(numero);
    }

    get numeroControleGabineteAtual(): number {
        return this.controleGabinete?.numero;
    }

    get idProcessoEscaninho() {
        return this.numeroProcesso;
    }

    get ehLegado() {
        const id = (this.origemProcesso as any)?.id || this.origemProcesso;
        const descricao = (this.origemProcesso as any)?.descricao;
        return id === 'L' || id === 'Legado' || descricao === 'Legado' || (this as any).origem === 'L';
    }

    get ehPje() {
        const id = (this.origemProcesso as any)?.id || this.origemProcesso;
        const descricao = (this.origemProcesso as any)?.descricao;
        return id === 'P' || id === 'PJe' || descricao === 'PJe' || (this as any).origem === 'P';
    }

    get ultimoMovimentoCNJ(): string {
        if (!this.ehPje) return '';

        // 1. Tentar extrair do array de movimentos carregados (ordenando por data decrescente)
        if (Array.isArray(this.movimentos) && this.movimentos.length > 0) {
            const movsOrdenados = [...this.movimentos].sort((m1, m2) => {
                const d1 = m1?.atualizadoEm ? new Date(m1.atualizadoEm).getTime() : 0;
                const d2 = m2?.atualizadoEm ? new Date(m2.atualizadoEm).getTime() : 0;
                return d2 - d1;
            });
            for (const m of movsOrdenados) {
                const txt = (m?.textoFinalInterno || (m as any)?.descricao || (m as any)?.texto || (m as any)?.nome || '')?.trim();
                if (txt) return txt;
            }
        }

        // 2. Tentar extrair de ultimoMovimentoProcessual
        const ultimoMov = this.ultimoMovimentoProcessual as any;
        if (ultimoMov) {
            const txt = (ultimoMov.textoFinalInterno || ultimoMov.descricao || ultimoMov.texto || ultimoMov.nome || ultimoMov.descricaoMovimento || '')?.trim();
            if (txt) return txt;
        }

        // 3. Tentar campos diretos que possam ter sido mapeados
        const movDireto = ((this as any).movimentoPje || (this as any).ultimoMovimento || (this as any).movimento);
        if (typeof movDireto === 'string' && movDireto.trim()) {
            return movDireto.trim();
        }

        return '';
    }

    get relatorAtual() {
        return this.orgaoJulgador?.sigla || this.orgaoJulgadorColegiado?.sigla
    }

    private normalizarTextoPauta(texto?: string): string {
        return (texto ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();
    }

    /**
     * Texto da tarefa no sistema de origem do processo:
     * - PJe: usa tarefaFluxoPje.tarefaAtual;
     * - eSIJ/Legado: usa situacaoDeInclusaoEmPauta (estado de pauta na origem).
     */
    get tarefaNoSistemaOrigem(): string {
        const tarefaPje = this.tarefaFluxoPje?.tarefaAtual;
        const tarefaEsij = typeof this.situacaoDeInclusaoEmPauta === 'string'
            ? this.situacaoDeInclusaoEmPauta
            : '';

        if (this.ehPje) {
            return tarefaPje || tarefaEsij || '';
        }

        return tarefaEsij || tarefaPje || '';
    }

    get tarefaAguardandoSessaoJulgamento(): boolean {
        const tarefaAtual = this.normalizarTextoPauta(this.tarefaNoSistemaOrigem);
        return tarefaAtual.includes('aguardando sessao de julgamento');
    }

    get confirmadoNaPautaDeJulgamento(): boolean {
        const textos = [
            this.tarefaFluxoPje?.tarefaAtual,
            this.situacaoDeInclusaoEmPauta,
            this.resultadoJulgamentoProcesso?.tipoSituacaoPautaDto?.tipoSituacaoPauta,
        ].map(texto => this.normalizarTextoPauta(texto));

        return textos.some(texto =>
            (texto.includes('confirmad') && texto.includes('pauta')) ||
            (texto.includes('incluid') && texto.includes('pauta'))
        );
    }

    get emFluxoEsperadoDoEscaninhoPauta(): boolean {
        return this.tarefaAguardandoSessaoJulgamento || this.confirmadoNaPautaDeJulgamento;
    }

    get aguardandoPautaOuSessao(): boolean {
        const situacaoPauta = typeof this.situacaoDeInclusaoEmPauta === 'string' ? this.situacaoDeInclusaoEmPauta : '';
        const textos = [this.tarefaFluxoPje?.tarefaAtual, situacaoPauta];
        const aguardando = textos.some(texto => {
            const t = (texto ?? '').toLowerCase();
            return t.includes('aguardando') && (t.includes('pauta') || t.includes('sessão') || t.includes('sessao'));
        });
        const codigoSituacao = this.resultadoJulgamentoProcesso?.tipoSituacaoPautaDto?.codigoTipoSituacaoPauta;
        return aguardando || codigoSituacao === 'AS';
    }

    /** Possui ao menos uma minuta de voto (voto, voto à águia ou voto de fase futura). */
    get temMinutaVoto(): boolean {
        return (this.minutas ?? []).some(minuta =>
            !!minuta?.numero && (
                minuta.tipo?.codigo === TipoMinuta.VOTO ||
                minuta.tipo?.codigo === TipoMinuta.VOTO_AGUIA ||
                minuta.tipo?.codigo === TipoMinuta.VOTO_FASE_FUTURA
            )
        );
    }

    /**
     * Processo enviado à pauta por despacho/decisão (GE-573) e que está fora do fluxo:
     * está aguardando pauta/sessão (PJe "Aguardando inclusão em pauta ou sessão" ou eSIJ
     * "Aguardando pauta") porém ainda não possui minuta de voto. A Secretaria impede a
     * inclusão em pauta sem voto, portanto o GE alerta que é necessário criar a minuta de voto.
     */
    get precisaMinutaVotoParaPauta(): boolean {
        return this.aguardandoPautaOuSessao && !this.temMinutaVoto;
    }


    get noFluxoCerto() {
        const gabinete = UsuarioStore.gabinete;
        const tarefa = this.tarefaFluxoPje;
        const ehPje = this.ehPje;
        const ehLegado = this.ehLegado;

        if (gabinete?.startsWith("CEJUSC") || gabinete?.startsWith("SEPREX") || gabinete?.startsWith("SETR") || gabinete === "SETPOESDC") {
            return true;
        }

        const processoPJeNoGabinete = ehPje &&
            (
                this.orgaoJulgador?.sigla === gabinete ||
                this.orgaoJulgadorColegiado?.sigla === gabinete ||
                this.orgaoJulgadorColegiado?.descricao?.toLowerCase() === "sem ojc cadastrado"
            );

        const processoLegadoNoGabinete = ehLegado && tarefa?.localizacao === gabinete;

        if (RouterStore.isEscaninhoJulgamento || RouterStore.isEscaninhoPauta) {
            return this.processoPautaFutura || tarefa?.aguardandoJulgamento;
        }

        if (RouterStore.isEscaninhoGerencial || RouterStore.isEscaninhoUsuario || RouterStore.isControleGabinete) {

            const pjeAguardandoAssinatura = ehPje &&
                tarefa?.tarefaAtual.toLowerCase().includes("assinar acórdão") &&
                processoPJeNoGabinete;

            if (!tarefa) {
                return true;
            }

            const noFluxoPje = tarefa.deveriaEstarNoGabineteEletronico &&
                !tarefa.aguardandoJulgamento &&
                !pjeAguardandoAssinatura &&
                processoPJeNoGabinete &&
                !ehLegado;

            const noFluxoLegado = !tarefa.aguardandoJulgamento &&
                !pjeAguardandoAssinatura &&
                processoLegadoNoGabinete &&
                ehLegado;

            return noFluxoPje || noFluxoLegado;
        }

        if (RouterStore.isAlgumEscaninho) {
            const noFluxoPje = tarefa?.deveriaEstarNoGabineteEletronico &&
                !tarefa?.aguardandoJulgamento &&
                !ehLegado &&
                processoPJeNoGabinete;

            const noFluxoLegado = !tarefa?.aguardandoJulgamento &&
                ehLegado &&
                processoLegadoNoGabinete;

            return noFluxoPje || noFluxoLegado;
        }

        if (!tarefa) {
            return true;
        }

        const noFluxoPje = tarefa.deveriaEstarNoGabineteEletronico &&
            !tarefa.aguardandoJulgamento &&
            !ehLegado &&
            processoPJeNoGabinete;

        const noFluxoLegado = !tarefa.aguardandoJulgamento &&
            ehLegado &&
            processoLegadoNoGabinete;

        return noFluxoPje || noFluxoLegado;
    }


    // noinspection JSUnusedGlobalSymbols
    toJSON() {
        return {
            ...this,
            minutas: this.minutas.map(minuta => ({
                ...minuta,
                processoPai: undefined
            })),
            minutaSelecionada: this.minutaSelecionada,
            tipoMinutaSelecionada: this.tipoMinutaSelecionada,
            numeroControleGabineteAtual: this.numeroControleGabineteAtual,
            numeroMinuta: this.minutaSelecionada,
            favorito: this.favorito,
            agravoProvido: this.agravoProvido,
            idProcessoEscaninho: undefined,
            _mostrar: undefined,
            _selecionado: undefined,
            _pendencia: undefined
        }
    }

    static limparProcesso(p: Processo) {
        p.minutas?.map(m => {
            m.movimentosCNJ?.map(mov => {
                mov.idProcesso = undefined;
            })
            m.movimentosCNJ = []
            m.categorias = []
        })
        p.minutas = [];
        p.background = undefined;
        p.backgroundPadrao = undefined;
        p.relator = undefined;
        p.relatorOriginal = undefined;
        p.peticoesPendentes = [];
        p.pecasPeticoesPendentes = [];
        p.controleGabinete = undefined;
    }
}
